import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import tw from 'twrnc';

import { useAppTheme } from '../design-system/ThemeProvider';
import { isScreenshotMode } from '../../lib/screenshotMode';
import GamificationNavigator from '../features/gamification/navigation/GamificationNavigator';
import { useLanguageStore } from '../features/quiz-core/languageStore';
import { t } from '../i18n/translations';
import { useGuestUser } from '../shared/contexts/GuestUserContext';
import AboutScreen from '../shared/ui/AboutScreen';
import { iqesColors } from '../shared/ui/iqesDesignSystem';
import LeaderboardScreen from '../shared/ui/LeaderboardScreen';
import ProfileScreen from '../shared/ui/ProfileScreen';
import SettingsScreen from '../shared/ui/SettingsScreen';

const Stack = createStackNavigator();
type MoreMenuIconName = keyof typeof Ionicons.glyphMap;

const MoreMenuScreen = () => {
  const navigation = useNavigation();
  const { language } = useLanguageStore();
  const { width } = useWindowDimensions();
  const { isGuest, endGuestSession } = useGuestUser();
  const { colors, isDark } = useAppTheme();
  const isWideLayout = width >= 840;

  const handleProfileEntry = () => {
    if (isGuest) {
      endGuestSession();
      return;
    }

    navigation.navigate('ProfileDetail' as never);
  };

  const menuItems = [
    {
      title: t('moreScreen.profile', language),
      subtitle: isGuest ? t('moreScreen.guestNoAccount', language) : t('moreScreen.profileSubtitle', language),
      icon: 'person-circle',
      screen: 'ProfileDetail',
      iconBg: isDark ? colors.primaryContainer : '#ECF4FF',
      iconColor: isDark ? colors.primaryLight : '#2563EB',
    },
    {
      title: t('moreScreen.progressAchievements', language),
      subtitle: t('moreScreen.progressSubtitle', language),
      icon: 'trophy',
      screen: 'GamificationDetail',
      iconBg: isDark ? colors.surfaceContainerHigh : '#F3E8FF',
      iconColor: isDark ? colors.primaryLight : '#7C3AED',
    },
    {
      title: t('moreScreen.settings', language),
      subtitle: t('moreScreen.settingsSubtitle', language),
      icon: 'settings',
      screen: 'SettingsDetail',
      iconBg: isDark ? colors.surfaceContainerHigh : '#EEF2F7',
      iconColor: isDark ? colors.onSurfaceVariant : '#4B5563',
    },
    // Store screenshot capture only: reachable entry to the premium paywall
    // (the 09_premium scene taps this; there is no user-facing Upgrade CTA here).
    ...(isScreenshotMode()
      ? [
          {
            title: 'Upgrade',
            subtitle: 'Premium plans',
            icon: 'star',
            screen: 'SubscriptionPlans',
            iconBg: isDark ? colors.surfaceContainerHigh : '#FEF3C7',
            iconColor: isDark ? colors.primaryLight : '#D97706',
          },
        ]
      : []),
  ];
  const [primaryItem, ...secondaryItems] = menuItems;
  const baseSurface = iqesColors.canvas;
  const cardSurface = 'rgba(8,10,24,0.92)';
  const cardBorder = 'rgba(246,190,58,0.34)';
  const cardChevronSurface = 'rgba(246,190,58,0.12)';
  const titleColor = iqesColors.ivory;
  const subtitleColor = 'rgba(255,248,232,0.7)';

  return (
    <View style={[tw`flex-1`, { backgroundColor: baseSurface }]}>
      <ScrollView
        style={tw`flex-1 px-6`}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <TouchableOpacity activeOpacity={0.92} onPress={handleProfileEntry}>
          <LinearGradient
            colors={[iqesColors.canvasRaised, '#2B123F', '#4C1D78']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              tw`rounded-3xl mt-6 mb-6`,
              {
                padding: 24,
                borderWidth: 1.3,
                borderColor: 'rgba(246,190,58,0.48)',
                shadowColor: iqesColors.gold,
                shadowOffset: { width: 0, height: 14 },
                shadowOpacity: 0.22,
                shadowRadius: 24,
                elevation: 10,
              },
            ]}
          >
            <View style={tw`flex-row items-start justify-between`}>
              <View style={{ flex: 1, paddingRight: 16 }}>
                <Text style={[tw`text-xs font-bold uppercase mb-3`, { color: iqesColors.goldSoft, letterSpacing: 1.4 }]}>
                  {t('moreScreen.title', language)}
                </Text>
                <View
                  style={{
                    alignSelf: 'flex-start',
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 999,
                    backgroundColor: 'rgba(246,190,58,0.13)',
                    borderWidth: 1,
                    borderColor: 'rgba(246,190,58,0.36)',
                    marginBottom: 12,
                  }}
                >
                  <Text style={{ color: iqesColors.goldSoft, fontSize: 12, fontWeight: '800', letterSpacing: 0.4 }}>
                    {isGuest ? t('quickActions.notSignedIn', language) : t('moreScreen.progressSubtitle', language)}
                  </Text>
                </View>
                <Text style={[tw`text-4xl font-bold mb-3`, { color: iqesColors.ivory, lineHeight: 42 }]}>
                  {t('moreScreen.profile', language)}
                </Text>
                <Text style={[tw`text-base`, { color: 'rgba(255,248,232,0.80)', lineHeight: 24 }]}>
                  {isGuest ? t('quickActions.guestNoAccount', language) : t('moreScreen.settingsSubtitle', language)}
                </Text>
              </View>
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 22,
                  backgroundColor: 'rgba(246,190,58,0.14)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 1,
                  borderColor: 'rgba(246,190,58,0.38)',
                }}
              >
                <Ionicons name="sparkles-outline" size={28} color={iqesColors.goldSoft} />
              </View>
            </View>
          </LinearGradient>
        </TouchableOpacity>
        <View style={tw`mb-4`}>
          <TouchableOpacity
            style={[
              tw`rounded-3xl p-6`,
              {
                backgroundColor: cardSurface,
                borderWidth: 1,
                borderColor: cardBorder,
                shadowColor: iqesColors.gold,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.14,
                shadowRadius: 14,
                elevation: 4,
              }
            ]}
            onPress={handleProfileEntry}
          >
            <View style={tw`flex-row items-center`}>
              <View
                style={[
                  tw`rounded-2xl p-4 mr-4`,
                  {
                    backgroundColor: primaryItem.iconBg,
                    shadowColor: primaryItem.iconColor,
                    shadowOffset: { width: 0, height: 3 },
                    shadowOpacity: 0.14,
                    shadowRadius: 6,
                    elevation: 4,
                  }
                ]}
              >
                <Ionicons name={primaryItem.icon as MoreMenuIconName} size={28} color={primaryItem.iconColor} />
              </View>
              <View style={tw`flex-1`}>
                <Text style={[tw`text-2xl font-bold mb-1`, { color: titleColor }]}>
                  {primaryItem.title}
                </Text>
                <Text style={[tw`text-sm leading-relaxed`, { color: subtitleColor }]}>
                  {primaryItem.subtitle}
                </Text>
              </View>
              <View style={[tw`rounded-xl p-2`, { backgroundColor: cardChevronSurface }]}>
                <Ionicons name="chevron-forward" size={24} color={subtitleColor} />
              </View>
            </View>
          </TouchableOpacity>
        </View>

        <View style={[tw`mb-8`, { flexDirection: isWideLayout ? 'row' : 'column', gap: 16 }]}>
          {secondaryItems.map((item, index) => (
            <TouchableOpacity
              key={index}
              style={[
                tw`rounded-3xl p-5`,
                {
                  flex: isWideLayout ? 1 : undefined,
                  backgroundColor: cardSurface,
                  borderWidth: 1,
                  borderColor: cardBorder,
                shadowColor: iqesColors.gold,
                  shadowOffset: { width: 0, height: 3 },
                  shadowOpacity: 0.12,
                  shadowRadius: 12,
                  elevation: 3,
                }
              ]}
              onPress={() => navigation.navigate(item.screen as never)}
            >
              <View style={tw`flex-row items-center`}>
                <View
                  style={[
                    tw`rounded-2xl p-4 mr-4`,
                    {
                      backgroundColor: item.iconBg,
                      shadowColor: item.iconColor,
                      shadowOffset: { width: 0, height: 3 },
                      shadowOpacity: 0.14,
                      shadowRadius: 6,
                      elevation: 4,
                    }
                  ]}
                >
                    <Ionicons name={item.icon as MoreMenuIconName} size={26} color={item.iconColor} />
                </View>
                <View style={tw`flex-1`}>
                  <Text style={[tw`text-xl font-bold mb-1`, { color: titleColor }]}>
                    {item.title}
                  </Text>
                  <Text style={[tw`text-sm leading-relaxed`, { color: subtitleColor }]}>
                    {item.subtitle}
                  </Text>
                </View>
                <View style={[
                  tw`rounded-xl p-2`,
                  {
                    backgroundColor: cardChevronSurface,
                  }
                ]}>
                  <Ionicons name="chevron-forward" size={24} color={subtitleColor} />
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const MoreNavigator = () => {
  const { language } = useLanguageStore();

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
      }}
    >
      <Stack.Screen
        name="MoreMenu"
        component={MoreMenuScreen}
        options={{
          title: t('moreScreen.title', language),
          headerShown: false, // Let tab header handle this
        }}
      />
      <Stack.Screen
        name="ProfileDetail"
        component={ProfileScreen}
        options={({ navigation }) => ({
          headerShown: true,
          headerTitle: () => {
            const { language } = useLanguageStore();
            return (
              <Text style={{
                fontWeight: 'bold',
                color: iqesColors.ivory,
                fontSize: 18,
              }}>
                {t('moreScreen.profileHeader', language)}
              </Text>
            );
          },
          headerStyle: {
            backgroundColor: iqesColors.canvas,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(246,190,58,0.38)',
            shadowColor: '#F6BE3A',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 3,
          },
          headerTintColor: iqesColors.gold,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => {
                try {
                  if (navigation.canGoBack()) {
                    navigation.goBack();
                  } else {
                    navigation.navigate('MoreMenu');
                  }
                } catch (error) {
                  console.warn('Navigation back failed:', error);
                  navigation.navigate('MoreMenu');
                }
              }}
              style={{
                marginLeft: 16,
                padding: 8,
                borderRadius: 20,
                backgroundColor: 'rgba(246,190,58,0.12)',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <Ionicons name="arrow-back" size={20} color={iqesColors.gold} />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen
        name="LeaderboardDetail"
        component={LeaderboardScreen}
        options={({ navigation }) => ({
          headerShown: true,
          headerTitle: () => {
            const { language } = useLanguageStore();
            return (
              <Text style={{
                fontWeight: 'bold',
                color: iqesColors.ivory,
                fontSize: 18,
              }}>
                {t('moreScreen.leaderboardHeader', language)}
              </Text>
            );
          },
          headerStyle: {
            backgroundColor: iqesColors.canvas,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(246,190,58,0.38)',
            shadowColor: '#F6BE3A',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 3,
          },
          headerTintColor: iqesColors.gold,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => {
                try {
                  if (navigation.canGoBack()) {
                    navigation.goBack();
                  } else {
                    navigation.navigate('MoreMenu');
                  }
                } catch (error) {
                  console.warn('Navigation back failed:', error);
                  navigation.navigate('MoreMenu');
                }
              }}
              style={{
                marginLeft: 16,
                padding: 8,
                borderRadius: 20,
                backgroundColor: 'rgba(246,190,58,0.12)',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <Ionicons name="arrow-back" size={20} color={iqesColors.gold} />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen
        name="GamificationDetail"
        component={GamificationNavigator}
        options={({ navigation }) => ({
          headerShown: true,
          headerTitle: () => {
            const { language } = useLanguageStore();
            return (
              <Text style={{
                fontWeight: 'bold',
                color: iqesColors.ivory,
                fontSize: 18,
              }}>
                {t('moreScreen.progressHeader', language)}
              </Text>
            );
          },
          headerStyle: {
            backgroundColor: iqesColors.canvas,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(246,190,58,0.38)',
            shadowColor: '#F6BE3A',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 3,
          },
          headerTintColor: iqesColors.gold,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => {
                try {
                  if (navigation.canGoBack()) {
                    navigation.goBack();
                  } else {
                    navigation.navigate('MoreMenu');
                  }
                } catch (error) {
                  console.warn('Navigation back failed:', error);
                  navigation.navigate('MoreMenu');
                }
              }}
              style={{
                marginLeft: 16,
                padding: 8,
                borderRadius: 20,
                backgroundColor: 'rgba(246,190,58,0.12)',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <Ionicons name="arrow-back" size={20} color={iqesColors.gold} />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen
        name="SettingsDetail"
        component={SettingsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          headerTitle: () => {
            const { language } = useLanguageStore();
            return (
              <Text style={{
                fontWeight: 'bold',
                color: iqesColors.ivory,
                fontSize: 18,
              }}>
                {t('moreScreen.settingsHeader', language)}
              </Text>
            );
          },
          headerStyle: {
            backgroundColor: iqesColors.canvas,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(246,190,58,0.38)',
            shadowColor: '#F6BE3A',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 3,
          },
          headerTintColor: iqesColors.gold,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => {
                try {
                  if (navigation.canGoBack()) {
                    navigation.goBack();
                  } else {
                    navigation.navigate('MoreMenu');
                  }
                } catch (error) {
                  console.warn('Navigation back failed:', error);
                  navigation.navigate('MoreMenu');
                }
              }}
              style={{
                marginLeft: 16,
                padding: 8,
                borderRadius: 20,
                backgroundColor: 'rgba(246,190,58,0.12)',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <Ionicons name="arrow-back" size={20} color={iqesColors.gold} />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen
        name="About"
        component={AboutScreen}
        options={{
          headerShown: false, // About screen has its own header
        }}
      />
    </Stack.Navigator>
  );
};

export default MoreNavigator;
